"use client";
import { useAtom, useAtomValue } from "jotai";
import { ReactFlowProvider } from "@xyflow/react";
import {
  BuiltInApp,
  RawCanvas,
  Session,
  Toggles,
  isKeyboardShortcutsModalOpenAtom,
  Account,
  currentAccountIdAtom,
} from "@zapier/canvas-state";
import { PAGE_VIEW_EVENT } from "@zapier/canvas-constants";
import { useEffect } from "react";

import { Canvas } from "../components/Canvas";
import { CanvasHydrator } from "../components/CanvasHydrator";
import { BuilderNav } from "../components/BuilderNav";
import { KeyboardShortcutsModal } from "../components/KeyboardShortcutsModal";
import { Notifications } from "../components/Notifications";
import { CanvasBackground } from "../components/CanvasBackground";
import { CanvasCopilotHydrator } from "../components/CanvasCopilotHydrator";
import useUpdateSelectedApps from "../hooks/useUpdateSelectedApps";

import styles from "./IframeEmbeddedCanvas.module.css";

export function ViewOnlyCanvas({
  canvas,
  tablesApp,
  interfacesApp,
  chatbotsApp,
  agentsApp,
  session,
  account,
  toggles,
  isAgentsEnabled,
}: {
  canvas: RawCanvas;
  tablesApp: BuiltInApp;
  interfacesApp: BuiltInApp;
  chatbotsApp: BuiltInApp;
  agentsApp: BuiltInApp;
  session: Session;
  account: Account;
  toggles: Toggles;
  isAgentsEnabled: boolean;
}) {
  useUpdateSelectedApps();
  useEmitPageViewEvent();

  return (
    <ReactFlowProvider>
      <CanvasHydrator
        canvas={canvas}
        tablesApp={tablesApp}
        interfacesApp={interfacesApp}
        chatbotsApp={chatbotsApp}
        agentsApp={agentsApp}
        isPublic={true}
        session={session}
        account={account}
        toggles={toggles}
        isAgentsEnabled={isAgentsEnabled}
      >
        <CanvasCopilotHydrator>
          <Notifications />
          <BuilderNav>
            <Page />
          </BuilderNav>
        </CanvasCopilotHydrator>
      </CanvasHydrator>
    </ReactFlowProvider>
  );
}

function Page() {
  const [isKeyboardShortcutsModalOpen, setIsKeyboardShortcutsModalOpen] =
    useAtom(isKeyboardShortcutsModalOpenAtom);
  return (
    <>
      {isKeyboardShortcutsModalOpen && (
        <KeyboardShortcutsModal
          onClosed={() => setIsKeyboardShortcutsModalOpen(false)}
        />
      )}
      <div className={styles.wrapper}>
        <Canvas>
          <CanvasBackground />
        </Canvas>
      </div>
    </>
  );
}

function useEmitPageViewEvent() {
  const currentAccountId = useAtomValue(currentAccountIdAtom);
  useEffect(() => {
    setTimeout(() => {
      window.EventToolkit.emit(
        {
          system_name: "canvas",
          account_id: currentAccountId,
        },
        PAGE_VIEW_EVENT,
      );
      // For some reason the cdn JS that we load with the event toolkit that populates
      // window.EventToolkit doesn't seem to be available immediately. If we wait, it is more likely to be available.
      // This is a temporary fix until we can figure out why the JS isn't available immediately.
    }, 100);
  }, [currentAccountId]);
}
